import React, { useState, useEffect } from 'react';
import { Key, Save, Check, AlertCircle } from 'lucide-react';
import { useStore } from '../lib/store';

export const ApiSettings = () => {
  const { user, apiKeys, apiKeysError, updateApiKeys } = useStore();
  const [keys, setKeys] = useState({
    openai: '',
    elevenlabs: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Update local state when apiKeys changes (e.g., on load or user change)
  useEffect(() => {
    if (apiKeys) {
      setKeys({
        openai: apiKeys.openai || '',
        elevenlabs: apiKeys.elevenlabs || ''
      });
    }
  }, [apiKeys]);

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      await updateApiKeys(keys);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving API keys:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Key className="w-6 h-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-amber-500">API Settings</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || !keys.openai || !keys.elevenlabs}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-900"
        >
          {showSuccess ? (
            <Check className="w-5 h-5" />
          ) : (
            <Save className={`w-5 h-5 ${isSaving ? 'animate-spin' : ''}`} />
          )}
          {showSuccess ? 'Saved!' : (isSaving ? 'Saving...' : 'Save Keys')}
        </button>
      </div>

      {apiKeysError && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2 text-red-500">
          <AlertCircle className="w-5 h-5" />
          <span>{apiKeysError}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-gray-300 mb-2">OpenAI API Key</label>
          <input
            type="password"
            value={keys.openai}
            onChange={(e) => setKeys(prev => ({ ...prev, openai: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
            placeholder="sk-..."
          />
        </div>

        <div>
          <label className="block text-gray-300 mb-2">ElevenLabs API Key</label>
          <input
            type="password"
            value={keys.elevenlabs}
            onChange={(e) => setKeys(prev => ({ ...prev, elevenlabs: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
            placeholder="Enter your ElevenLabs API key"
          />
        </div>

        {(!keys.openai || !keys.elevenlabs) && (
          <div className="p-4 bg-amber-500/10 border border-amber-500 rounded-lg text-amber-500">
            Please add both API keys to enable AI features
          </div>
        )}
      </div>
    </div>
  );
};